import * as React from "react";
import { format, subDays, startOfWeek, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from "date-fns";
import { Calendar as CalendarIcon, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PRESETS = [
  ["Today", () => { const d = new Date(); return { from: d, to: d }; }],
  ["Yesterday", () => { const d = subDays(new Date(), 1); return { from: d, to: d }; }],
  ["This Week", () => ({ from: startOfWeek(new Date()), to: new Date() })],
  ["Last 7 Days", () => ({ from: subDays(new Date(), 6), to: new Date() })],
  ["Last 28 Days", () => ({ from: subDays(new Date(), 27), to: new Date() })],
  ["Last 30 Days", () => ({ from: subDays(new Date(), 29), to: new Date() })],
  ["Last 90 Days", () => ({ from: subDays(new Date(), 89), to: new Date() })],
  ["This Month", () => ({ from: startOfMonth(new Date()), to: new Date() })],
  ["Last Month", () => { const d = subMonths(new Date(), 1); return { from: startOfMonth(d), to: endOfMonth(d) }; }],
  ["Last 12 Months", () => ({ from: subMonths(new Date(), 11), to: new Date() })],
  ["Last Calendar Year", () => { const d = subYears(new Date(), 1); return { from: startOfYear(d), to: endOfYear(d) }; }],
];

export function DateRangePicker({ date, onDateChange, placeholder = "Pick a date range", className, align = "start" }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const active = (range) => date?.from && date?.to && format(date.from, "yyyy-MM-dd") === format(range.from, "yyyy-MM-dd") && format(date.to, "yyyy-MM-dd") === format(range.to, "yyyy-MM-dd");
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("w-[280px] justify-start text-left font-normal", !date && "text-muted-foreground", className)}>
          <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
          {date?.from ? `${format(date.from, "LLL dd, y")} - ${date.to ? format(date.to, "LLL dd, y") : ""}` : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x">
          <div className="flex flex-col p-3 gap-1 md:w-[160px] h-[350px] overflow-y-auto">
            <div className="font-medium text-sm mb-2 px-2">Presets</div>
            {PRESETS.map(([label, getRange]) => {
              const range = getRange();
              return <Button key={label} variant={active(range) ? "secondary" : "ghost"} className={cn("justify-start font-normal text-left px-2", active(range) && "font-medium")} onClick={() => { onDateChange(getRange()); setIsOpen(false); }}>{label}{active(range) && <Check className="ml-auto h-4 w-4" />}</Button>;
            })}
          </div>
          <div className="p-1">
            <Calendar initialFocus mode="range" defaultMonth={date?.from} selected={date} onSelect={onDateChange} numberOfMonths={2} disabled={(d) => d > new Date() || d < new Date("1900-01-01")} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
